import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, RefreshCw, ChevronRight, LogOut } from "lucide-react";
import { api, clearKey, intakeLinkStore } from "./api";
import { StatusPill, CopyField } from "./ui.jsx";

export default function JobsList({ onLock }) {
  const [jobs, setJobs] = useState(null);
  const [store, setStore] = useState(null);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [newJob, setNewJob] = useState(null);
  const [listErr, setListErr] = useState(null);
  const nav = useNavigate();

  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  const load = async () => {
    api.storageStatus().then(setStore).catch(() => setStore({ available: false }));
    try {
      const d = await api.listJobs();
      setJobs(d.jobs);
      setListErr(null);
    } catch (e) {
      setListErr(e.message);
      toast.error(e.message);
      setJobs((prev) => prev); // TEST-057: keep what was shown; never present an outage as "no jobs"
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Enter a valid client email address");
      return;
    }
    setNewJob(null);
    try {
      const d = await api.createJob(label.trim(), email.trim());
      intakeLinkStore.set(d.jobId, d.intakeLink);
      setNewJob(d);
      setLabel("");
      setEmail("");
      toast.success("Intake created");
      if (d.intakeEmail) (d.intakeEmail.status === "sent" ? toast.success : toast.error)(
        d.intakeEmail.status === "sent" ? `Intake link emailed to ${d.intakeEmail.to}` : `Intake email ${d.intakeEmail.status}: ${d.intakeEmail.detail}`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-10 backdrop-blur bg-slate-950/80 border-b border-slate-800">
        <div className="mx-auto max-w-3xl px-4 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-amber-400/80">Alias · JoRevGen</p>
            <h1 className="text-xl font-semibold">Jobs</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="refresh-jobs" className="rounded-lg p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={() => { clearKey(); onLock(); }} data-testid="sign-out" className="rounded-lg p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5 space-y-4">
        {store && store.available === false && (
          <p data-testid="storage-unavailable" className="rounded-xl bg-rose-500/15 ring-1 ring-rose-500/40 px-4 py-3 text-sm text-rose-200">
            Job storage is unavailable right now. Jobs can't be shown or changed, and nothing new will be saved until it's back. Existing records are safe — try Refresh in a minute.
          </p>
        )}
        {store && store.legacyJobs && store.legacyJobs.length > 0 && (
          <p data-testid="storage-legacy" className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/40 px-4 py-3 text-sm text-amber-200">
            {store.legacyJobs.length} local job folder(s) are not in durable storage and are hidden until imported: {store.legacyJobs.join(", ")}.
          </p>
        )}
        {store && store.conflicts > 0 && (
          <p data-testid="storage-conflicts" className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/40 px-4 py-3 text-sm text-amber-200">
            {store.conflicts} stored file(s) differed from the local copy at startup. The saved copy was kept; the other copy is set aside for review.
          </p>
        )}
        <button
          onClick={() => { setCreating((v) => !v); setNewJob(null); }}
          data-testid="new-sale-toggle"
          className="w-full flex items-center justify-center gap-2 rounded-2xl bg-amber-500 py-3.5 font-semibold text-slate-950 hover:bg-amber-400 transition-colors"
        >
          <Plus className="h-5 w-5" /> New sale
        </button>

        {creating && (
          <div data-testid="create-panel" className="rounded-2xl bg-slate-900/70 ring-1 ring-slate-800 p-4 space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">Client email <span className="text-amber-400/80">(required)</span></label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="create-email-input"
                placeholder="e.g. owner@kilmartinheating.com"
                className="w-full rounded-xl bg-slate-950 ring-1 ring-slate-700 focus:ring-amber-500 px-3.5 py-3 outline-none transition"
              />
              <p className="mt-1 text-[11px] text-slate-500">The secure intake link is emailed to this address automatically.</p>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">Client / business label <span className="text-slate-600">(optional)</span></label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="create-label-input"
                placeholder="e.g. Kilmartin Heating"
                className="w-full rounded-xl bg-slate-950 ring-1 ring-slate-700 focus:ring-amber-500 px-3.5 py-3 outline-none transition"
              />
            </div>
            <button onClick={create} data-testid="create-job-submit" className="w-full rounded-xl bg-slate-100 py-3 font-semibold text-slate-950 hover:bg-white transition-colors">
              Create intake + link
            </button>
            {newJob && (
              <div className="space-y-2 pt-1" data-testid="new-job-result">
                <p className="text-sm font-medium text-emerald-300">Intake created</p>
                <p className="text-xs text-slate-400">
                  {newJob.intakeEmail
                    ? (newJob.intakeEmail.status === "sent"
                      ? <span data-testid="new-job-email-status">Intake link emailed to {newJob.intakeEmail.to} (accepted by the mail server). </span>
                      : <span data-testid="new-job-email-status" className="text-rose-300">Intake email {newJob.intakeEmail.status}: {newJob.intakeEmail.detail}. </span>)
                    : <span data-testid="new-job-email-status">No client email given — nothing was emailed. </span>}
                  You can also copy the link below (job <span className="font-mono">{newJob.jobId}</span>).
                </p>
                <CopyField label="Intake link — send to client" value={newJob.intakeLink} testid="new-intake-link" />
              </div>
            )}
          </div>
        )}

        <div className="space-y-3" data-testid="jobs-list">
          {jobs === null && <p className="text-slate-500 text-sm px-1" data-testid="jobs-list-status">{store && store.available === false ? "Jobs can't be loaded while storage is unavailable." : listErr ? `${listErr} Use refresh to try again.` : "Loading…"}</p>}
          {jobs && jobs.length === 0 && <p className="text-slate-500 text-sm px-1">No jobs yet. Start with New sale.</p>}
          {jobs && jobs.map((j) => (
            <button
              key={j.jobId}
              onClick={() => nav(`/job/${j.jobId}`)}
              data-testid={`job-row-${j.jobId}`}
              className="w-full text-left rounded-2xl bg-slate-900/60 ring-1 ring-slate-800 hover:ring-amber-500/40 p-4 transition group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {j.displayName.name || <span className="text-slate-500">Unnamed — awaiting intake</span>}
                  </p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">{j.jobId}</p>
                </div>
                <StatusPill status={j.status} live={j.nextAction?.key === "completed"} reverify={j.nextAction?.key === "reverify"} />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-sm text-amber-300">
                  {j.nextAction.label}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-amber-300 transition" />
              </div>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
